import { type CSSProperties, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useLocale } from '../../hooks/useLocale';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import animateValue from '../../utils/animateValue';
import { stripCurrencySymbol, toDecimals } from '../../utils/helpers';
import { formatNumber, isGermanNumberFormat } from '../../utils/numberFormat';
import { PlusIcon } from '../BalancePlusIcon/PlusIcon';

import './BalancePanel.scss';
import { BalancePlusContext } from './BalancePlusContext';
import { fitLiveValueToSlot } from './fitLiveValueToSlot';
import { useFitBalanceValueSize } from './useFitBalanceValueSize';
import { useShowDualBalance } from './useShowDualBalance';

export const Balance = observer(() => {
  const { i18n } = useTranslation();
  const { walletStore, coinTypeStore, gameStore } = useStores();
  const { localeCurrencySymbol } = useLocale();
  const onPress = useContext(BalancePlusContext);

  const enableBalanceAnimation = useFlag('balance_animation_enabled', true);

  const animationDuration = useMemo(() => (enableBalanceAnimation ? 1500 : 100), [enableBalanceAnimation]);

  const ref = useRef<HTMLSpanElement>(null);
  const cashRef = useRef<HTMLSpanElement>(null);
  const cashSlotRef = useRef<HTMLSpanElement>(null);
  const coinSlotRef = useRef<HTMLSpanElement>(null);
  const cancelCoinFitRef = useRef<(() => void) | null>(null);
  const cancelCashFitRef = useRef<(() => void) | null>(null);

  const [prev, setPrev] = useState(0);
  const [prevCash, setPrevCash] = useState(0);

  const { currentCustomValue, currentCoinValue } = walletStore;
  const cash = walletStore.balance.cash;

  const showDualBalance = useShowDualBalance();

  // Mirror what `animateValue` renders (SC forced to 2 decimals, CC rounded to whole coins) so the
  // static text and the count-up never disagree.
  const formatCoin = (value: number) =>
    coinTypeStore.coinType === 'GC' ? formatNumber(Math.round(value), undefined, 'en-US') : toDecimals(value);
  const coinValueText = formatCoin(currentCustomValue ?? currentCoinValue ?? 0);
  const cashValueText = toDecimals(cash);

  // The slot is `display: none` while the cash poll reloads, so it measures 0 and a longer cash value
  // that arrives meanwhile would stay clipped once revealed — re-fit when it becomes visible.
  const valueSize = useFitBalanceValueSize(
    cashSlotRef,
    coinSlotRef,
    `${cashValueText}|${coinValueText}|${gameStore.isReloadingCash}`,
    showDualBalance,
  );
  const valueSizeStyle = { '--balance-value-size': `${valueSize}px` } as CSSProperties;

  // The live spans have no React-managed text: `animateValue` owns their content, so a re-render can
  // never paint the target value for a frame before the count-up resets it to the previous one. Seed
  // each span with the value its animation starts from whenever it mounts (the cash span can remount
  // when the dual-balance flag flips).
  const seededCoinElRef = useRef<HTMLSpanElement | null>(null);
  const seededCoinTypeRef = useRef(coinTypeStore.coinType);
  const seededCashElRef = useRef<HTMLSpanElement | null>(null);
  const seededLanguageRef = useRef(i18n.language);
  useLayoutEffect(() => {
    // A language switch changes the number format with no balance change, so no count-up runs to repaint it.
    const languageChanged = i18n.language !== seededLanguageRef.current;
    seededLanguageRef.current = i18n.language;
    // The coin span is reused across a CC/SC toggle, so a new element isn't the only reason to
    // re-seed: with equal balances no count-up runs, and the old format would stay on screen.
    if (
      ref.current &&
      (languageChanged ||
        ref.current !== seededCoinElRef.current ||
        coinTypeStore.coinType !== seededCoinTypeRef.current)
    ) {
      seededCoinElRef.current = ref.current;
      seededCoinTypeRef.current = coinTypeStore.coinType;
      ref.current.textContent = formatCoin(prev);
    }
    if (cashRef.current && (languageChanged || cashRef.current !== seededCashElRef.current)) {
      seededCashElRef.current = cashRef.current;
      cashRef.current.textContent = toDecimals(prevCash);
    }
  });

  useEffect(() => {
    const curr = currentCustomValue ?? currentCoinValue ?? 0;
    if (curr !== prev && ref.current) {
      setPrev(curr);
      animateValue(ref.current, prev, curr, animationDuration, coinTypeStore.coinType);
      cancelCoinFitRef.current?.();
      cancelCoinFitRef.current = fitLiveValueToSlot(ref.current, animationDuration);
    }
  }, [prev, coinTypeStore.coinType, currentCustomValue, currentCoinValue, animationDuration]);

  // Count-up the cash balance the same way SC/CC animate. `showDualBalance` is a dependency so the
  // effect re-fires once the cash span mounts (the LD flag can resolve after the first commit),
  // otherwise the value-change that filled the balance would have happened while cashRef was null.
  useEffect(() => {
    if (cash !== prevCash && cashRef.current) {
      setPrevCash(cash);
      animateValue(cashRef.current, prevCash, cash, animationDuration, 'SC', true);
      cancelCashFitRef.current?.();
      cancelCashFitRef.current = fitLiveValueToSlot(cashRef.current, animationDuration);
    }
  }, [showDualBalance, cash, prevCash, animationDuration]);

  useEffect(
    () => () => {
      cancelCoinFitRef.current?.();
      cancelCashFitRef.current?.();
    },
    [],
  );

  const coinContent = (
    <>
      <div className="balance-panel-left">{coinTypeStore.coinType === 'GC' ? 'CC' : 'SC'}</div>
      <span ref={coinSlotRef} className="balance-panel-balance-slot">
        <span aria-hidden className="balance-panel-balance balance-panel-sizer">
          {coinValueText}
        </span>
        <span
          ref={ref}
          className={classNames(
            'balance-panel-balance',
            'balance-panel-live',
            showDualBalance && coinTypeStore.coinType === 'SC' && 'balance-panel-sc-dual-balance',
          )}
          data-testid="lobby-balance-bar"
          id="balance"
        />
      </span>
    </>
  );

  if (!showDualBalance) {
    return coinContent;
  }

  const cashSign = (
    <span className="balance-panel-cash-sign" data-testid="lobby-cash-sign">
      {stripCurrencySymbol(localeCurrencySymbol())}
    </span>
  );
  const germanFormat = isGermanNumberFormat();

  return (
    <>
      <div className="balance-panel-cash" data-testid="lobby-cash-balance" style={valueSizeStyle}>
        {!germanFormat && cashSign}
        {gameStore.isReloadingCash && (
          <span aria-label="Updating balance" className="balance-panel-cash-reload" data-testid="lobby-cash-reload">
            <span className="balance-panel-cash-reload-dot" />
            <span className="balance-panel-cash-reload-dot" />
            <span className="balance-panel-cash-reload-dot" />
          </span>
        )}
        {/* Keep cashRef mounted (hide the slot while reloading) — unmounting it mid-animateValue rAF
            races React's removeChild during the poll's isReloadingCash swap and crashes the SC lobby. */}
        <span
          ref={cashSlotRef}
          className="balance-panel-cash-slot"
          style={gameStore.isReloadingCash ? { display: 'none' } : undefined}
        >
          <span aria-hidden className="balance-panel-cash-amount balance-panel-sizer">
            {cashValueText}
          </span>
          <span
            ref={cashRef}
            className="balance-panel-cash-amount balance-panel-live"
            data-testid="lobby-cash-amount"
          />
        </span>
        {germanFormat && cashSign}
      </div>
      <div className="balance-panel-sc-pill" data-testid="lobby-sc-pill" style={valueSizeStyle}>
        {coinContent}
        {onPress && (
          <div className="balance-panel-sc-pill-plus">
            <PlusIcon onPress={onPress} />
          </div>
        )}
      </div>
    </>
  );
});
